import { useMemo } from 'react';
import { useQuery, UseQueryOptions } from '@tanstack/react-query';
import { useIntl } from 'react-intl';

import { ERROR, PENDING, SUCCESS } from '../../design-system/constants';
import { APICallStatus, StepName } from '../../design-system/types';
import { useAppSelector } from '../../shared/redux';
import { getDraftMessageStatus } from '../discussion/selectors';
import { useGetAgents } from '../agent/hooks';
import { QUERY_STALE_TIMES } from '../../shared/providers/react-query-provider';
import { AllTranslationKeys } from '../../shared/intl/types';

import { fetchDossier, getTripProject } from './api';
import {
    DossierData,
    DossierId,
    Heat,
    MarketCode,
    SerializedDossier,
    TaskDefinition,
} from './types';

export const getDossierQueryKey = (dossierId: DossierId) => ['dossier', dossierId];

const useGetDossier = (dossierId: DossierId, options?: UseQueryOptions<SerializedDossier>) => {
    return useQuery<SerializedDossier>({
        queryKey: getDossierQueryKey(dossierId),
        queryFn: () => fetchDossier(dossierId),
        enabled: !!dossierId,
        staleTime: QUERY_STALE_TIMES.DOSSIER,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        ...options,
    });
};

export const useGetDossierData = (dossierId: DossierId) => {
    const { data } = useGetDossier(dossierId);

    // eslint-disable-next-line complexity -- data extraction with optional chaining, inherently branchy
    return useMemo<DossierData | null>(() => {
        if (!data) return null;

        const dossier = data.dossier[dossierId];
        const memo = data.dossierMemo?.[dossierId] || null;
        const participants = data.dossierParticipants?.[dossierId] || null;
        const availableTasks = data.dossierAvailableTasks?.[dossierId]?.bySteps || null;
        const suggestedTask = data.dossierAvailableTasks?.[dossierId]?.suggested || null;
        const customerCareCrisisTicket = data.dossierCustomerCareCrisisTicket?.[dossierId] || null;
        const contractVouchers = data.contractVouchers?.[dossierId] || [];
        const contractDeferAgreement = data.contractDeferAgreement?.[dossierId] || null;
        const preTripReview = data.preTripReview?.[dossierId] || null;
        const inTripReview = data.inTripReview?.[dossierId] || null;
        const travellerInformation = data.travellerInformation?.[dossierId] || null;

        return {
            dossier,
            memo,
            participants,
            availableTasks,
            suggestedTask,
            customerCareCrisisTicket,
            contractVouchers,
            contractDeferAgreement,
            preTripReview,
            inTripReview,
            travellerInformation,
        };
    }, [data, dossierId]);
};

export const useCurrentTaskByDossierId = (dossierId: DossierId) => {
    const { dossier } = useGetDossierData(dossierId) || {};
    const { step, task } = dossier || {};
    const { definition, dueDate, note } = task || {};

    return useMemo(
        () => ({
            step,
            task: definition,
            dueDate,
            note: note || '',
        }),
        [step, definition, dueDate, note],
    );
};

export const useDossierAndAgentRequestStatus = (
    dossierId: DossierId,
): APICallStatus | undefined => {
    const { status: agentStatus } = useGetAgents();
    const { status: dossierStatus } = useGetDossier(dossierId);

    const draftMessageStatus = useAppSelector(getDraftMessageStatus(dossierId));

    return useMemo(() => {
        if (
            dossierStatus === PENDING ||
            agentStatus === 'pending' ||
            draftMessageStatus === PENDING
        ) {
            return PENDING;
        }
        if (dossierStatus === ERROR || agentStatus === 'error') {
            return ERROR;
        }
        if (dossierStatus === SUCCESS && agentStatus === 'success') {
            return SUCCESS;
        }
        return undefined;
    }, [dossierStatus, agentStatus, draftMessageStatus]);
};

export const useGetAgentByDossierId = (dossierId: DossierId) => {
    const { data: agents } = useGetAgents();

    const { dossier } = useGetDossierData(dossierId) || {};

    return useMemo(() => {
        if (!dossier || !agents) return;

        return agents?.[dossier.agentId];
    }, [dossier, agents]);
};

export const useHeatLevelLabelByDossierId = (
    dossierId: DossierId,
): 'none' | 'hot' | 'warm' | 'cold' | undefined => {
    const { heatLevel } = useGetDossierData(dossierId)?.dossier || {};

    return useMemo(() => {
        if (!heatLevel) return;

        const heatLabelMap = {
            [Heat.NONE]: 'none' as const,
            [Heat.HOT]: 'hot' as const,
            [Heat.WARM]: 'warm' as const,
            [Heat.COLD]: 'cold' as const,
        };

        return heatLabelMap[heatLevel];
    }, [heatLevel]);
};

export const useGetStepOptionsByDossierId = (dossierId: DossierId) => {
    const { availableTasks, dossier } = useGetDossierData(dossierId) || {};
    const { formatMessage } = useIntl();

    return useMemo(() => {
        if (!dossier?.step) {
            return;
        }
        const stepToOption = (step: string) => ({
            label: formatMessage({
                id: `steps.${step}.name` as AllTranslationKeys,
            }),
            value: step as StepName,
        });
        return {
            currentStepValue: stepToOption(dossier.step),
            stepOptions: availableTasks ? Object.keys(availableTasks).map(stepToOption) : [],
        };
    }, [availableTasks, dossier?.step, formatMessage]);
};

export const useGetTaskOptionsByDossierId = (dossierId: DossierId) => {
    const { availableTasks, dossier } = useGetDossierData(dossierId) || {};
    const { formatMessage } = useIntl();

    return useMemo(() => {
        if (!dossier?.task) {
            return;
        }
        const taskToOption = (task: TaskDefinition) => ({
            label: formatMessage({
                id: `tasks.${task}.name` as AllTranslationKeys,
            }),
            value: task,
        });

        const tasks = availableTasks?.[dossier?.step];
        const taskOptions = tasks?.map(taskToOption) ?? [];
        return {
            currentTaskValue: taskToOption(dossier.task.definition),
            taskOptions: taskOptions,
        };
    }, [availableTasks, dossier?.task, dossier?.step, formatMessage]);
};

const getTripProjectQueryKey = (dossierId: DossierId, marketCode: MarketCode) => [
    'trip-project',
    dossierId,
    marketCode,
];

/**
 * Fetches trip project fields for a dossier from the initial request (RFQ).
 *
 * Returns structured fields with translated keys and values based on the dossier's market.
 * The query only executes when both dossierId and marketCode are defined.
 *
 * @param dossierId - The dossier ID
 * @param marketCode - The dossier's market code (e.g., 'FR', 'GB', 'CH')
 * @returns Query result with TripProject data
 */
export const useGetTripProject = (dossierId: DossierId, marketCode: MarketCode | undefined) => {
    return useQuery({
        queryKey: getTripProjectQueryKey(dossierId, marketCode!),
        queryFn: () => getTripProject(dossierId, marketCode!),
        enabled: !!dossierId && !!marketCode,
        staleTime: QUERY_STALE_TIMES.DOSSIER,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
    });
};
